import { supabase } from "@/lib/supabase";
import { callRpc } from "@/lib/api/rpc";

// Carrier adapter registry (frontend).
//
// Core shipping code never branches on `carrier`. Provider-specific display
// metadata and capability flags live here, behind a single boundary. The
// authoritative API behaviour lives in the `carrier-sync` edge function and
// the backend RPCs. Secrets are referenced server-side by `credential_ref`
// and never exposed to the browser.

export interface CarrierAccountRow {
  id: string;
  org_id: string;
  carrier: string;
  name: string;
  external_account_id: string | null;
  auth_status: string;
  credential_ref: string | null;
  enabled: boolean;
  capabilities: Record<string, boolean> | null;
  health_status: string;
  connection_error: string | null;
  last_success_at: string | null;
  last_error_at: string | null;
  config: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CarrierAdapterMeta {
  carrier: string;
  label: string;
  icon: string;
  description: string;
  capabilities: Record<string, boolean>;
  multiInstance: boolean;
}

export const carrierRegistry: CarrierAdapterMeta[] = [
  {
    carrier: "auspost",
    label: "Australia Post",
    icon: "ri-mail-send-line",
    description: "Australia Post Parcel Post, Express Post and international services.",
    capabilities: { test_connection: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true },
    multiInstance: true,
  },
  {
    carrier: "dhl",
    label: "DHL",
    icon: "ri-flight-takeoff-line",
    description: "DHL Express worldwide shipping.",
    capabilities: { test_connection: true, validate_address: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true, create_return: true },
    multiInstance: true,
  },
  {
    carrier: "fedex",
    label: "FedEx",
    icon: "ri-global-line",
    description: "FedEx Express and Ground shipping.",
    capabilities: { test_connection: true, validate_address: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true, create_return: true },
    multiInstance: true,
  },
  {
    carrier: "ups",
    label: "UPS",
    icon: "ri-roadster-line",
    description: "UPS worldwide express and ground services.",
    capabilities: { test_connection: true, validate_address: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true, create_return: true },
    multiInstance: true,
  },
  {
    carrier: "sendle",
    label: "Sendle",
    icon: "ri-plant-line",
    description: "Sendle carbon-neutral parcel delivery.",
    capabilities: { test_connection: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true },
    multiInstance: true,
  },
  {
    carrier: "startrack",
    label: "StarTrack",
    icon: "ri-truck-line",
    description: "StarTrack premium Australian freight.",
    capabilities: { test_connection: true, get_rates: true, create_shipment: true, get_label: true, get_tracking: true, cancel_shipment: true },
    multiInstance: true,
  },
  {
    carrier: "manual",
    label: "Manual / Offline",
    icon: "ri-edit-box-line",
    description: "Record postage manually for carriers without a live integration.",
    capabilities: {},
    multiInstance: true,
  },
];

export function carrierMeta(carrier: string | null | undefined): CarrierAdapterMeta | undefined {
  if (!carrier) return undefined;
  return carrierRegistry.find((c) => c.carrier === carrier);
}

export function carrierLabel(carrier: string | null | undefined): string {
  return carrierMeta(carrier)?.label ?? carrier ?? "—";
}

export interface RateSource {
  rate_source: string;
}

export const RATE_SOURCE_LABEL: Record<string, string> = {
  internal: "Internal estimate",
  live: "Live carrier rate",
  manual: "Manual",
  aggregator: "Aggregator",
};

export async function registerCarrierAccount(params: {
  carrier: string;
  name: string;
  external_account_id?: string | null;
  config?: Record<string, unknown> | null;
  credential_ref?: string | null;
}) {
  return callRpc<{ ok: boolean; id?: string; error?: string }>("register_carrier_account", {
    p_carrier: params.carrier,
    p_name: params.name,
    p_external_account_id: params.external_account_id ?? null,
    p_config: params.config ?? null,
    p_credential_ref: params.credential_ref ?? null,
  });
}

export async function updateCarrierAccount(params: {
  accountId: string;
  name?: string | null;
  external_account_id?: string | null;
  config?: Record<string, unknown> | null;
  credential_ref?: string | null;
  enabled?: boolean | null;
}) {
  return callRpc<{ ok: boolean; error?: string }>("update_carrier_account", {
    p_account_id: params.accountId,
    p_name: params.name ?? null,
    p_external_account_id: params.external_account_id ?? null,
    p_config: params.config ?? null,
    p_credential_ref: params.credential_ref ?? null,
    p_enabled: params.enabled ?? null,
  });
}

export async function testCarrierConnection(accountId: string) {
  const { data, error } = await supabase.functions.invoke("carrier-sync", {
    body: { action: "test-connection", accountId },
  });
  if (error) throw error;
  return data as { ok: boolean; state?: string; error?: string; result?: { connected?: boolean } };
}

export async function processCarrierJobs() {
  const { data, error } = await supabase.functions.invoke("carrier-sync", {
    body: { action: "process-jobs" },
  });
  if (error) throw error;
  return data as { ok: boolean; processed: number; results: { job_id: string; ok: boolean; errorClass?: string }[] };
}